import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  TextField,
  MenuItem,
  Autocomplete,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  CircularProgress,
  Alert,
  Divider,
  Tooltip,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Close as CloseIcon } from '@mui/icons-material';
import { employeeSettingsApi, userLabel } from '../../services/expenseMastersApi';
import useExpenseLookups, { withCurrent } from '../../components/expense/useExpenseLookups';

// HR admin: management approvers + home country. Accounts: wallet currency.
// The backend enforces these field permissions; the UI just disables what you can't change.
const EmployeeExpenseSettings = ({ isAdmin, isAccounts }) => {
  const { users, countries, currencies } = useExpenseLookups(['users', 'countries', 'currencies']);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [employeeId, setEmployeeId] = useState('');
  const [current, setCurrent] = useState(null);
  const [currentLoading, setCurrentLoading] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setPage(0); }, [debouncedSearch]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await employeeSettingsApi.list({ search: debouncedSearch, page: page + 1, limit: rowsPerPage });
      setRows(res.data || []);
      setTotal(res.pagination?.totalItems || 0);
    } catch (error) {
      toast.error(`Failed to load employee settings: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, page, rowsPerPage]);

  useEffect(() => { load(); }, [load]);

  // Load the selected employee's current settings
  useEffect(() => {
    if (!employeeId) {
      setCurrent(null);
      return;
    }
    let cancelled = false;
    setCurrentLoading(true);
    employeeSettingsApi.get(employeeId)
      .then((res) => { if (!cancelled) setCurrent(res.data); })
      .catch((error) => toast.error(error.message))
      .finally(() => { if (!cancelled) setCurrentLoading(false); });
    return () => { cancelled = true; };
  }, [employeeId]);

  const openDialog = (id = '') => {
    setEmployeeId(id);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEmployeeId('');
    setCurrent(null);
  };

  // Keep current approvers selectable (and removable) even if they are no longer active
  const approverOptions = [
    ...users,
    ...[...(current?.reportingApprovers || []), ...(current?.managementApprovers || [])]
      .filter((a, i, all) => a._id && !users.some((u) => u.value === a._id) && all.findIndex((x) => x._id === a._id) === i)
      .map((a) => ({ value: a._id, label: `${userLabel(a)} (inactive)` })),
  ].filter((u) => u.value !== employeeId);

  const initialValues = {
    reportingApprovers: (current?.reportingApprovers || []).map((u) => u._id || u),
    managementApprovers: (current?.managementApprovers || []).map((u) => u._id || u),
    homeCountryId: current?.homeCountryId?._id || '',
    walletCurrency: current?.walletCurrency || '',
  };

  const schema = Yup.object().shape({
    reportingApprovers: Yup.array().max(10, 'At most 10 approvers')
      .test('not-self', 'An employee cannot be their own approver', (v) => !(v || []).includes(employeeId)),
    managementApprovers: Yup.array().max(10, 'At most 10 approvers')
      .test('not-self', 'An employee cannot be their own approver', (v) => !(v || []).includes(employeeId)),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    // Send only the fields this user is allowed to change
    const body = {};
    if (isAdmin) {
      body.reportingApprovers = values.reportingApprovers;
      body.managementApprovers = values.managementApprovers;
      body.homeCountryId = values.homeCountryId;
    }
    if (isAccounts) body.walletCurrency = values.walletCurrency;

    try {
      const res = await employeeSettingsApi.save(employeeId, body);
      toast.success(res.message || 'Saved');
      closeDialog();
      load();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const changeLog = current?.changeLog || [];

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        HR admins set management approvers and home country. Accounts set the wallet currency.
      </Alert>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
        <TextField size="small" placeholder="Search employee name / email" value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 300, '& .MuiInputBase-root': { fontSize: '0.8rem' }, '& .MuiInputBase-input': { py: '6px' } }} />
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => openDialog()} sx={{ textTransform: 'none' }}>
          Configure Employee
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.main' }}>
              {['Employee', 'Reporting Approvers', 'Management Approvers', 'Home Country', 'Wallet Currency', 'Last Updated By', 'Actions'].map((h) => (
                <TableCell key={h} sx={{ color: 'white', fontWeight: 600 }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} align="center"><CircularProgress size={28} /></TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center">
                <Typography variant="body2" color="text.secondary">No employee settings yet</Typography>
              </TableCell></TableRow>
            ) : rows.map((r) => (
              <TableRow key={r._id} sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                <TableCell>{userLabel(r.employee)}</TableCell>
                <TableCell>
                  {(r.reportingApprovers || []).length
                    ? r.reportingApprovers.map((u) => `${u.firstName || ''} ${u.lastName || ''}`.trim()).join(', ')
                    : <Typography variant="caption" color="text.secondary">User Hierarchy manager</Typography>}
                </TableCell>
                <TableCell>{(r.managementApprovers || []).map((u) => `${u.firstName || ''} ${u.lastName || ''}`.trim()).join(', ')}</TableCell>
                <TableCell>{r.homeCountryId?.name || ''}</TableCell>
                <TableCell>{r.walletCurrency || ''}</TableCell>
                <TableCell>{r.updatedBy ? `${r.updatedBy.firstName || ''} ${r.updatedBy.lastName || ''}`.trim() : ''}</TableCell>
                <TableCell>
                  <Tooltip title="Edit">
                    <IconButton size="small" color="primary" onClick={() => openDialog(r.employee?._id)}><EditIcon /></IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div" count={total} page={page} rowsPerPage={rowsPerPage}
          onPageChange={(e, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </TableContainer>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" component="span">Employee Expense Settings</Typography>
          <IconButton onClick={closeDialog}><CloseIcon /></IconButton>
        </DialogTitle>
        <Formik
          key={`${employeeId}-${current?._id || 'new'}-${currentLoading}`}
          enableReinitialize
          initialValues={initialValues}
          validationSchema={schema}
          onSubmit={handleSubmit}
        >
          {({ values, errors, setFieldValue, isSubmitting }) => (
            <Form>
              <DialogContent>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Autocomplete
                      options={users}
                      value={users.find((u) => u.value === employeeId) || null}
                      onChange={(e, opt) => setEmployeeId(opt ? opt.value : '')}
                      getOptionLabel={(o) => o.label || ''}
                      isOptionEqualToValue={(o, v) => o.value === v.value}
                      renderInput={(params) => <TextField {...params} label="Employee" required />}
                    />
                  </Grid>

                  {currentLoading && (
                    <Grid item xs={12} sx={{ textAlign: 'center' }}><CircularProgress size={24} /></Grid>
                  )}

                  {employeeId && !currentLoading && (
                    <>
                      <Grid item xs={12}>
                        <Autocomplete
                          multiple
                          options={approverOptions}
                          value={approverOptions.filter((u) => values.reportingApprovers.includes(u.value))}
                          onChange={(e, opts) => setFieldValue('reportingApprovers', opts.map((o) => o.value))}
                          getOptionLabel={(o) => o.label || ''}
                          isOptionEqualToValue={(o, v) => o.value === v.value}
                          disabled={!isAdmin}
                          renderInput={(params) => (
                            <TextField {...params} label="Reporting Approvers"
                              error={Boolean(errors.reportingApprovers)}
                              helperText={errors.reportingApprovers || (!isAdmin
                                ? 'Managed by HR admin'
                                : 'Leave empty to use the manager from User Hierarchy')} />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Autocomplete
                          multiple
                          options={approverOptions}
                          value={approverOptions.filter((u) => values.managementApprovers.includes(u.value))}
                          onChange={(e, opts) => setFieldValue('managementApprovers', opts.map((o) => o.value))}
                          getOptionLabel={(o) => o.label || ''}
                          isOptionEqualToValue={(o, v) => o.value === v.value}
                          disabled={!isAdmin}
                          renderInput={(params) => (
                            <TextField {...params} label="Management Approvers"
                              error={Boolean(errors.managementApprovers)}
                              helperText={errors.managementApprovers || (!isAdmin ? 'Managed by HR admin' : '')} />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField select fullWidth label="Home Country" value={values.homeCountryId}
                          onChange={(e) => setFieldValue('homeCountryId', e.target.value)}
                          disabled={!isAdmin} helperText={!isAdmin ? 'Managed by HR admin' : ''}>
                          <MenuItem value=""><em>None</em></MenuItem>
                          {withCurrent(countries, current?.homeCountryId?._id, current?.homeCountryId?.name).map((c) => (
                            <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField select fullWidth label="Wallet Currency" value={values.walletCurrency}
                          onChange={(e) => setFieldValue('walletCurrency', e.target.value)}
                          disabled={!isAccounts} helperText={!isAccounts ? 'Managed by Accounts' : ''}>
                          <MenuItem value=""><em>None</em></MenuItem>
                          {withCurrent(currencies, current?.walletCurrency).map((c) => (
                            <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                          ))}
                        </TextField>
                      </Grid>

                      {changeLog.length > 0 && (
                        <Grid item xs={12}>
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="subtitle2" gutterBottom>Change history</Typography>
                          {changeLog.slice(-10).reverse().map((entry, i) => (
                            <Typography key={i} variant="caption" display="block" color="text.secondary">
                              {new Date(entry.changedAt).toLocaleString()} - {userLabel(entry.changedBy) || 'Unknown'}:{' '}
                              {entry.changes.map((c) => c.field).join(', ')}
                            </Typography>
                          ))}
                        </Grid>
                      )}
                    </>
                  )}
                </Grid>
              </DialogContent>
              <DialogActions sx={{ p: 3 }}>
                <Button onClick={closeDialog} disabled={isSubmitting} sx={{ textTransform: 'none' }}>Cancel</Button>
                <Button type="submit" variant="contained" disabled={isSubmitting || !employeeId || currentLoading} sx={{ textTransform: 'none' }}>
                  {isSubmitting ? 'Saving...' : 'Save'}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </Box>
  );
};

export default EmployeeExpenseSettings;
